import React from 'react';
import { motion } from 'motion/react';
import {
  GraduationCap,
  BookOpen,
  Trophy,
  Flame,
  Clock,
  Brain,
  Video,
  Upload,
  DollarSign,
  BarChart3,
  Users,
  Shield,
  MessageSquare,
  VideoIcon,
  CheckCircle,
  ArrowRight,
  Check,
  Star,
  Award,
  Target,
  Zap,
  Sparkles,
  Play,
  Search,
  Filter,
  Smartphone,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PDFDownloadButton from '../components/PDFDownloadButton';
import '../styles/somolearn.css';

const userRoles = [
  {
    title: 'Student',
    icon: GraduationCap,
    description: 'Watch lessons, take quizzes, earn points and compete for scholarships',
    color: 'student',
  },
  {
    title: 'Teacher',
    icon: BookOpen,
    description: 'Upload lessons, earn money based on views and engagement',
    color: 'teacher',
  },
  {
    title: 'Parent',
    icon: Users,
    description: 'Monitor children\'s progress and manage subscriptions',
    color: 'parent',
  },
  {
    title: 'Admin',
    icon: Shield,
    description: 'Manage tutors, scholarships, and platform content',
    color: 'admin',
  },
];

const studentFeatures = [
  {
    title: 'Lesson Library',
    icon: BookOpen,
    color: 'purple',
    description: 'Access thousands of recorded lessons from real teachers across all subjects.',
    features: [
      'Browse by subject (Math, Science, English, etc.)',
      'Filter by grade level (Grade 7-12 or University)',
      'Powerful search functionality',
      'Video player with progress tracking',
      'AI-generated quizzes per lesson',
    ],
  },
  {
    title: 'Progress & Gamification',
    icon: Trophy,
    color: 'gold',
    description: 'Stay motivated with points, streaks, and rewards as you learn.',
    features: [
      'Earn points for watching lessons',
      'Complete quizzes to boost your score',
      'Maintain daily learning streaks',
      '48-hour bonus for new content',
      'Compete on leaderboards',
    ],
  },
  {
    title: 'AI Quiz Generation',
    icon: Brain,
    color: 'blue',
    description: 'Test your knowledge with smart quizzes generated by AI.',
    features: [
      'Auto-generated using SOMO AI',
      'Instant feedback with explanations',
      'Score tracking across all subjects',
      'Adaptive difficulty levels',
      'Share results with friends',
    ],
  },
];

const gamificationFeatures = [
  {
    title: 'Points System',
    icon: Star,
    description: 'Earn points for every lesson watched and quiz completed. Redeem for rewards!',
  },
  {
    title: 'Daily Streaks',
    icon: Flame,
    description: 'Build learning habits with daily streaks. Miss a day? Use streak freezes!',
  },
  {
    title: '48-Hour Bonus',
    icon: Clock,
    description: 'Watch new lessons within 48 hours of upload for extra bonus points.',
  },
];

const teacherFeatures = [
  {
    title: 'Upload Lessons',
    icon: Upload,
    description: 'Share your knowledge with video lessons and notes',
  },
  {
    title: 'Track Earnings',
    icon: DollarSign,
    description: 'Views × K0.50 base rate with bonuses',
  },
  {
    title: 'Record In-App',
    icon: Video,
    description: 'Record lessons directly with built-in tools',
  },
  {
    title: 'Analytics',
    icon: BarChart3,
    description: 'Track views, downloads, and ratings',
  },
];

const aiFeatures = [
  {
    title: 'Smart Quiz Generation',
    icon: Sparkles,
    description: 'SOMO AI creates relevant questions from each lesson automatically.',
  },
  {
    title: 'Instant Feedback',
    icon: Zap,
    description: 'Get immediate explanations for wrong answers to learn faster.',
  },
  {
    title: 'Adaptive Learning',
    icon: Target,
    description: 'Questions adjust to your skill level for optimal challenge.',
  },
];

const expertTypes = ['Doctors', 'Engineers', 'Developers', 'Scientists', 'Business Experts'];

const SomoLearnPage = () => {
  return (
    <div className="somo-page" id="somolearn-content">
      <Header />
      
      {/* Hero Section */}
      <section className="somo-hero">
        <div className="container-custom">
          <div className="somo-hero-wrapper">
            <div className="somo-hero-content">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="somo-badge"
              >
                <GraduationCap size={16} />
                Mobile-First Offline Learning
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                SOMO<span className="text-gradient-somo"> Learn</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="somo-subtitle"
              >
                Never miss a class. Access recorded lessons from real teachers, 
                take AI-powered quizzes, earn points, and compete for scholarships — 
                all designed to work even in low-connectivity environments.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="somo-hero-buttons"
              >
                <a href="#contact" className="btn-somo-primary">
                  Get Started <ArrowRight size={16} />
                </a>
                <a href="#features" className="btn-somo-secondary">
                  Explore Features
                </a>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="hero-highlights-somo"
              >
                <div className="highlight-item-somo">
                  <CheckCircle size={20} />
                  <span>Works Offline</span>
                </div>
                <div className="highlight-item-somo">
                  <CheckCircle size={20} />
                  <span>AI-Powered Quizzes</span>
                </div>
                <div className="highlight-item-somo">
                  <CheckCircle size={20} />
                  <span>Earn Scholarships</span>
                </div>
              </motion.div>
            </div>
            
            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="somo-hero-image"
            >
              <div className="phone-mockup-group-somo">
                <div className="phone-mockup-somo phone-front">
                  <div style={{ 
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    padding: '2rem',
                    height: '500px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    <GraduationCap size={60} style={{ marginBottom: '1rem' }} />
                    <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>SOMO Learn</h3>
                    <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Never Miss a Class</p>
                    <div style={{ 
                      marginTop: '2rem',
                      display: 'flex',
                      gap: '1rem',
                      flexWrap: 'wrap',
                      justifyContent: 'center'
                    }}>
                      <div style={{ 
                        background: 'rgba(255,255,255,0.2)',
                        padding: '0.75rem 1rem',
                        borderRadius: '10px',
                        fontSize: '0.8rem'
                      }}>
                        <Trophy size={20} style={{ marginBottom: '0.25rem' }} />
                        <div>1,250 pts</div>
                      </div>
                      <div style={{ 
                        background: 'rgba(255,255,255,0.2)',
                        padding: '0.75rem 1rem',
                        borderRadius: '10px',
                        fontSize: '0.8rem'
                      }}>
                        <Flame size={20} style={{ marginBottom: '0.25rem' }} />
                        <div>7 day streak</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="somo-hero-bg">
          <div className="somo-blob-1" />
          <div className="somo-blob-2" />
        </div>
      </section>
      
      {/* User Roles Section */}
      <section className="somo-roles">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-somo"
          >
            <span className="section-tag-somo">User Roles</span>
            <h2>Built for Everyone</h2>
            <p>SOMO serves students, teachers, parents, and administrators with tailored experiences</p>
          </motion.div>
          
          <div className="roles-grid">
            {userRoles.map((role, index) => (
              <motion.div
                key={role.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="role-card"
              >
                <div className={`role-icon ${role.color}`}>
                  <role.icon size={32} />
                </div>
                <h3>{role.title}</h3>
                <p>{role.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Student Features Section */}
      <section className="somo-student-features" id="features">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-somo"
          >
            <span className="section-tag-somo">Student Features</span>
            <h2>Learn Your Way</h2>
            <p>Powerful tools designed to help students succeed at their own pace</p>
          </motion.div>
          
          <div className="student-features-grid">
            {studentFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="student-feature-card"
              >
                <div className="student-feature-header">
                  <div className={`feature-icon-somo ${feature.color}`}>
                    <feature.icon size={24} />
                  </div>
                  <h3>{feature.title}</h3>
                </div>
                <p>{feature.description}</p>
                <ul className="feature-list-somo">
                  {feature.features.map((item, i) => (
                    <li key={i}>
                      <Check size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Gamification Section */}
      <section className="somo-gamification">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-somo light"
          >
            <span className="section-tag-somo light">Gamification</span>
            <h2>Stay Motivated</h2>
            <p>Learning should be fun! Earn rewards and compete with friends</p>
          </motion.div>
          
          <div className="gamification-grid">
            {gamificationFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="gamification-card"
              >
                <div className="gamification-icon">
                  <feature.icon size={32} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Teacher Section */}
      <section className="somo-teacher">
        <div className="container-custom">
          <div className="teacher-content">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="teacher-text"
            >
              <span className="section-tag-somo">For Teachers</span>
              <h2>Teach & Earn</h2>
              <p>
                Share your knowledge with thousands of students and earn money 
                based on views. Upload lessons easily with our built-in recording 
                and editing tools.
              </p>
              
              <ul className="teacher-benefits">
                <li><Check size={20} /> Earn K0.50 per view base rate</li>
                <li><Check size={20} /> 24-hour upload bonus for timely content</li>
                <li><Check size={20} /> Record lessons directly in-app</li>
                <li><Check size={20} /> Trim and compress videos easily</li>
                <li><Check size={20} /> Track performance with detailed analytics</li>
              </ul>
              
              <a href="#contact" className="btn-somo-primary">
                Become a Teacher <ArrowRight size={16} />
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="teacher-features-grid"
            >
              {teacherFeatures.map((feature, index) => (
                <div key={feature.title} className="teacher-feature-box">
                  <h4>
                    <feature.icon size={18} />
                    {feature.title}
                  </h4>
                  <p>{feature.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Tutoring Section */}
      <section className="somo-tutoring">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-somo"
          >
            <span className="section-tag-somo">SOMO Tutoring</span>
            <h2>1-on-1 Expert Sessions</h2>
            <p>Connect with verified professionals for personalized tutoring</p>
          </motion.div>
          
          <div className="tutoring-grid">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="tutoring-card"
            >
              <h3><Award size={24} /> SOMO Experts</h3>
              <p>
                Our verified experts include professionals from various fields 
                who are passionate about education and mentorship.
              </p>
              <div className="expert-badges">
                {expertTypes.map((type) => (
                  <span key={type} className="expert-badge">
                    <CheckCircle size={14} /> {type}
                  </span>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="tutoring-card"
            >
              <h3><VideoIcon size={24} /> Session Types</h3>
              <p>
                Choose the tutoring format that works best for your learning style.
              </p>
              <ul className="session-types">
                <li>
                  <div className="session-icon">
                    <Video size={20} />
                  </div>
                  <div>
                    <h4>Video Call</h4>
                    <p>Face-to-face sessions via Google Meet integration</p>
                  </div>
                </li>
                <li>
                  <div className="session-icon">
                    <MessageSquare size={20} />
                  </div>
                  <div>
                    <h4>In-App Chat</h4>
                    <p>Real-time messaging for quick questions and help</p>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* AI Section */}
      <section className="somo-ai">
        <div className="container-custom">
          <div className="ai-content-somo">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="ai-text-somo"
            >
              <span className="section-tag-somo light">AI-Powered</span>
              <h2>Smart Learning with SOMO AI</h2>
              <p>
                SOMO uses advanced AI to create personalized quizzes and provide 
                instant feedback, helping you learn more effectively.
              </p>
              
              <div className="ai-features-somo">
                {aiFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="ai-feature-item-somo"
                  >
                    <div className="ai-feature-icon-somo">
                      <feature.icon size={20} />
                    </div>
                    <div>
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="ai-visual-somo"
            >
              <div className="quiz-preview">
                <h4><Brain size={18} /> AI-Generated Quiz</h4>
                <div className="quiz-question">
                  <p>What is the derivative of x²?</p>
                  <div className="quiz-options">
                    <div className="quiz-option">
                      <span>A</span> x
                    </div>
                    <div className="quiz-option correct">
                      <span>B</span> 2x ✓
                    </div>
                    <div className="quiz-option">
                      <span>C</span> x²
                    </div>
                    <div className="quiz-option">
                      <span>D</span> 2x²
                    </div>
                  </div>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '1rem' }}>
                  <Sparkles size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Explanation: The power rule states d/dx(xⁿ) = nxⁿ⁻¹
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="somo-cta" id="contact">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="cta-content-somo"
          >
            <h2>Start Learning Today</h2>
            <p>
              Join thousands of students across Zambia and Africa who are 
              transforming their education with SOMO Learn.
            </p>
            <div className="cta-buttons-somo">
              <a href="mailto:sales@voxitconsulting.media" className="btn-somo-primary btn-white">
                <Smartphone size={18} /> Get the App
              </a>
              <a href="mailto:sales@voxitconsulting.media" className="btn-somo-secondary btn-outline-white">
                Contact Us
              </a>
            </div>
            <div className="app-stores">
              <span className="store-badge">
                <Play size={18} /> Google Play
              </span>
              <span className="store-badge">
                <Smartphone size={18} /> App Store
              </span>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
      <PDFDownloadButton 
        contentId="somolearn-content" 
        fileName="VoxIT-SOMO-Learn" 
        buttonText="Download PDF"
      />
    </div>
  );
};

export default SomoLearnPage;
